import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';

import { API_URL as API } from '../config/api';

// Fallback offers if API returns empty
const FALLBACK_OFFERS = [
  {
    _id: '1',
    discount: '15% OFF',
    title: 'Morning Saver',
    description: 'Discount for early morning football bookings. Get on the field before the crowd does.',
    color: '#084734',
  },
  {
    _id: '2',
    discount: '10% OFF',
    title: 'Cricket Weekday Deal',
    description: 'Special weekday discount for cricket slots. More savings for your practice sessions.',
    color: '#161616',
  },
];

export default function OffersSection() {
  const [offers, setOffers] = useState([]);
  const [selectedClaimOffer, setSelectedClaimOffer] = useState(null);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/offers`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setOffers(data);
      })
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, []);

  const displayOffers = offers;

  const handleClaimOffer = (offer) => {
    // If the offer has a list of multiple turfs, open picker modal
    if (offer.turfs && offer.turfs.length > 1) {
      setSelectedClaimOffer(offer);
      return;
    }
    
    // Otherwise, redirect immediately to the single turf
    const singleTurf = offer.turfs && offer.turfs.length === 1 ? offer.turfs[0] : null;
    const turfId = singleTurf?.id || singleTurf?._id || offer.turf_id?._id || offer.turf_id?.id || offer.turf_id;
    
    if (!turfId || turfId === 'undefined' || turfId === 'null') {
      alert('This offer is valid for all venues. Please select a venue to proceed!');
      navigate('/explore');
      return;
    }
    navigate(`/turf/${turfId}`, { state: { appliedOffer: offer } });
  };

  return (
    <section style={{ padding: '4rem 0' }}>
      {/* Section Header */}
      <div style={{ marginBottom: '2.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <span style={{ display: 'inline-block', width: '28px', height: '2px', background: '#CEF17B' }}></span>
          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#084734', textTransform: 'uppercase', letterSpacing: '2px' }}>
            Save more
          </span>
        </div>
        <h2 style={{
          fontSize: '2.2rem', fontWeight: '800', color: '#161616', marginBottom: '8px',
          fontFamily: "'Sora', sans-serif",
        }}>
          Exclusive <span style={{ color: '#084734' }}>Offers</span>
        </h2>
        <p style={{ color: '#374151', fontSize: '0.95rem', fontFamily: "'DM Sans', sans-serif" }}>
          Limited time deals for our regular players
        </p>
      </div>

      {/* Offer Cards */}
      {displayOffers.length === 0 ? (
        <div style={{
          padding: '2.5rem', background: '#FAFCF8', border: '1.5px solid #EEF2E6',
          borderRadius: '20px', textAlign: 'center', margin: '0 auto', maxWidth: '500px'
        }}>
          <h4 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#161616', marginBottom: '6px' }}>No Active Offers Right Now</h4>
          <p style={{ fontSize: '0.9rem', color: '#64748B' }}>Check back soon for exclusive discounts and promotions!</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem',
        }}>
          {displayOffers.map((offer, index) => (
          <div key={offer._id || index} style={{
            background: index % 2 === 0 ? '#084734' : '#161616',
            borderRadius: '20px',
            padding: '2.5rem',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            {/* Decorative circle */}
            <div style={{
              position: 'absolute', bottom: '-40px', right: '-40px',
              width: '180px', height: '180px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
            }} />
            <div style={{
              position: 'absolute', bottom: '20px', right: '20px',
              width: '100px', height: '100px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.03)',
            }} />

            <div>
              {/* Discount badge */}
              <div style={{
                display: 'inline-block',
                background: '#CEF17B', color: '#084734',
                borderRadius: '20px', padding: '5px 14px',
                fontWeight: '800', fontSize: '0.78rem',
                marginBottom: '1.25rem',
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {offer.discount || offer.discount_value || '15% OFF'}
              </div>

              {/* Venue details */}
              {offer.turfs && offer.turfs.length > 0 ? (
                <div style={{ fontSize: '0.85rem', color: '#CEF17B', fontWeight: '800', marginBottom: '8px', opacity: 0.9 }}>
                  🏟️ {offer.turfs.length === 1 
                    ? `${offer.turfs[0].name} ${offer.turfs[0].location ? `· ${offer.turfs[0].location}` : offer.turfs[0].city ? `· ${offer.turfs[0].city}` : ''}`
                    : `Valid at ${offer.turfs.length} venues (${offer.turfs.map(t => t.name).join(', ')})`
                  }
                </div>
              ) : (offer.turf_id && offer.turf_id.name && (
                <div style={{ fontSize: '0.85rem', color: '#CEF17B', fontWeight: '800', marginBottom: '8px', opacity: 0.9 }}>
                  🏟️ {offer.turf_id.name} {offer.turf_id.location ? `· ${offer.turf_id.location}` : offer.turf_id.city ? `· ${offer.turf_id.city}` : ''}
                </div>
              ))}

              <h3 style={{
                fontSize: '1.5rem', fontWeight: '800', color: 'white',
                marginBottom: '10px', fontFamily: "'Sora', sans-serif",
              }}>
                {offer.title}
              </h3>

              <p style={{
                fontSize: '0.9rem', color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.6, marginBottom: '1.25rem',
                maxWidth: '380px', fontFamily: "'DM Sans', sans-serif",
              }}>
                {offer.description}
              </p>
            </div>

            <div>
              {/* Date ranges */}
              {(offer.valid_from || offer.valid_until) && (
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>
                  ⏳ {offer.valid_from ? `Valid: ${new Date(offer.valid_from).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''} 
                  {offer.valid_from && offer.valid_until ? ' to ' : 'Expires: '}
                  {offer.valid_until ? new Date(offer.valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ongoing'}
                </div>
              )}

              <button 
                onClick={() => handleClaimOffer(offer)}
                style={{
                  background: 'transparent',
                  color: '#CEF17B',
                  border: '1.5px solid #CEF17B',
                  padding: '10px 24px', borderRadius: '50px',
                  cursor: 'pointer', fontWeight: '700',
                  fontSize: '0.88rem',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  transition: 'all 0.2s',
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#CEF17B'; e.currentTarget.style.color = '#084734'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#CEF17B'; }}
              >
                Claim Offer →
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* MULTIPLE VENUES SELECTION MODAL */}
      {selectedClaimOffer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          WebkitBackdropFilter: 'blur(6px)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: 'white',
            width: '90%',
            maxWidth: '480px',
            borderRadius: '24px',
            padding: '2rem',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            border: '1.5px solid #EEF2E6',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#161616', margin: 0, fontFamily: "'Sora', sans-serif" }}>Choose Venue</h3>
              <button 
                onClick={() => setSelectedClaimOffer(null)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#111827', display: 'flex', alignItems: 'center', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>
            
            <p style={{ color: '#374151', fontSize: '0.88rem', fontWeight: '500', marginBottom: '1.5rem', lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
              The offer <strong>"{selectedClaimOffer.title}"</strong> is valid at multiple venues. Please select one to book:
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '240px', overflowY: 'auto', paddingRight: '4px', marginBottom: '1.5rem' }}>
              {selectedClaimOffer.turfs.map(turf => (
                <div 
                  key={turf.id || turf._id}
                  onClick={() => {
                    navigate(`/turf/${turf.id || turf._id}`, { state: { appliedOffer: selectedClaimOffer } });
                    setSelectedClaimOffer(null);
                  }}
                  style={{
                    padding: '1rem',
                    borderRadius: '16px',
                    border: '1.5px solid #EEF2E6',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    transition: 'all 0.2s',
                    background: '#F8FAF7',
                    fontFamily: "'DM Sans', sans-serif"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.border = '1.5px solid #CEF17B';
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.boxShadow = '0 6px 15px rgba(0,0,0,0.03)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.border = '1.5px solid #EEF2E6';
                    e.currentTarget.style.background = '#F8FAF7';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontWeight: '800', color: '#161616', fontSize: '0.95rem' }}>{turf.name}</span>
                  <span style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: '600' }}>
                    {turf.location ? `${turf.location}, ` : ''}{turf.city}
                  </span>
                </div>
              ))}
            </div>
            
            <button 
              onClick={() => setSelectedClaimOffer(null)}
              style={{
                width: '100%',
                background: '#161616',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: '12px',
                fontWeight: '700',
                fontSize: '0.9rem',
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif"
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
